const CORNERS = [
	{ property: 'borderTopLeftRadius', horizontal: 'borderLeftWidth', vertical: 'borderTopWidth' },
	{ property: 'borderTopRightRadius', horizontal: 'borderRightWidth', vertical: 'borderTopWidth' },
	{ property: 'borderBottomRightRadius', horizontal: 'borderRightWidth', vertical: 'borderBottomWidth' },
	{ property: 'borderBottomLeftRadius', horizontal: 'borderLeftWidth', vertical: 'borderBottomWidth' },
] as const

/**
 * Match the overlay's corners to the host's own, so an effect that paints to its
 * edges cannot lay a square corner over a rounded card. Read rather than
 * hardcoded: the radius comes from whatever the host resolves — a schmancy
 * radius token, a utility class, an inline value — and hosts differ.
 *
 * Each corner is carried across as its own longhand, so a host with four
 * different corners, an elliptical radius, or a percentage keeps its exact
 * shape instead of collapsing to one number. The host's radius is on its border
 * box while the overlay sits on the padding box, so a px radius shrinks by that
 * edge's border width — the inner curve, not the outer one.
 */
export function syncOverlayRadius(overlay: HTMLElement, host: HTMLElement): void {
	const hostStyle = getComputedStyle(host)

	for (const corner of CORNERS) {
		const inset = [
			Number.parseFloat(hostStyle[corner.horizontal]) || 0,
			Number.parseFloat(hostStyle[corner.vertical]) || 0,
		]

		overlay.style[corner.property] = hostStyle[corner.property]
			.split(/\s+/)
			.map((radius, axis) =>
				radius.endsWith('px') ? `${Math.max(0, Number.parseFloat(radius) - inset[axis])}px` : radius,
			)
			.join(' ')
	}
}

export function createOverlayContainer(className: string, host?: HTMLElement): HTMLDivElement {
	const overlay = document.createElement('div')
	overlay.className = `art-overlay ${className}`
	overlay.style.cssText = `
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
		z-index: 1000;
		overflow: hidden;
	`

	if (host) syncOverlayRadius(overlay, host)

	return overlay
}

export function createSVG(): SVGSVGElement {
	const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
	svg.setAttribute('width', '100%')
	svg.setAttribute('height', '100%')
	svg.setAttribute('viewBox', '0 0 100 100')
	svg.setAttribute('preserveAspectRatio', 'none')
	svg.style.cssText = `
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	`
	return svg
}
